import { useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Navigate } from "react-router-dom";
import {
  useChangePlan,
  useCreatePortalSession,
  usePreviewPlanChange,
} from "~/api/billing/billing";
import { PreviewPlanChangeBodyOrganizationPlan, UserRole } from "~/api/model";
import {
  getGetOrganizationQueryKey,
  useGetOrganization,
} from "~/api/organizations/organizations";
import { DisplayFor } from "~/components/permissions/DisplayFor";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { PlanChangeModal } from "~/features/billing/components/PlanChangeModal";
import { PricingCard } from "~/features/billing/components/PricingCard";
import { PRICING_TIERS } from "~/features/billing/constants";
import { useOrganizationParams } from "~/helpers";

export const SettingsBilling: FC = () => {
  const queryClient = useQueryClient();
  const { organizationId } = useOrganizationParams();
  const { data: organizationInfo, isLoading: isOrgLoading } =
    useGetOrganization(organizationId as string);

  const [targetPlan, setTargetPlan] =
    useState<PreviewPlanChangeBodyOrganizationPlan | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { mutate: openPortal, isPending: isPortalLoading } =
    useCreatePortalSession();
  const {
    mutate: previewMutate,
    data: preview,
    isPending: isLoadingPreview,
    reset: resetPreview,
  } = usePreviewPlanChange();
  const { mutate: executeChangePlan, isPending: isChangingPlan } =
    useChangePlan();

  const isCustomPlan = !!(organizationInfo as Record<string, unknown>)
    ?.customPlan;
  const hasActiveStatus =
    (organizationInfo as Record<string, unknown>)?.serviceStatus == "ACTIVE";

  if (isOrgLoading) {
    return null;
  }

  if (isCustomPlan || !hasActiveStatus) {
    return <Navigate to="../account" replace />;
  }

  const currentPlan = detectCurrentPlan(organizationInfo);
  const currentPlanIndex = currentPlan
    ? (PLAN_ORDER as string[]).indexOf(currentPlan)
    : -1;

  const handleSelectPlan = (tierId: string) => {
    const tier = PRICING_TIERS.find((t) => t.id === tierId);
    if (!tier || tier.plan === currentPlan) return;
    setTargetPlan(tier.plan as PreviewPlanChangeBodyOrganizationPlan);
    setModalOpen(true);
    resetPreview();
    previewMutate({
      organizationId: organizationId as string,
      data: {
        organizationPlan: tier.plan as PreviewPlanChangeBodyOrganizationPlan,
      },
    });
  };

  const handleConfirmChange = () => {
    if (!targetPlan) return;
    executeChangePlan(
      {
        organizationId: organizationId as string,
        data: {
          organizationPlan: targetPlan as PreviewPlanChangeBodyOrganizationPlan,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getGetOrganizationQueryKey(organizationId as string),
          });
          setModalOpen(false);
          setTargetPlan(null);
        },
      }
    );
  };

  const handleOpenPortal = () => {
    openPortal(
      { organizationId: organizationId as string },
      {
        onSuccess: (data) => {
          if (data.portalUrl) {
            window.open(data.portalUrl, "_blank");
          }
        },
      }
    );
  };

  return (
    <div className="flex-1 space-y-6 w-full max-w-[924px]">
      <div>
        <h3 className="text-lg font-medium">
          <FormattedMessage
            id="settings.billing.title"
            defaultMessage="Billing"
          />
        </h3>
        <p className="text-sm text-muted-foreground">
          <FormattedMessage
            id="settings.billing.description"
            defaultMessage="Manage your subscription plan and billing details."
          />
        </p>
      </div>

      <DisplayFor roles={[UserRole.SUPERADMIN, UserRole.OWNER]}>
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3">
            {PRICING_TIERS.map((tier) => {
              const isCurrentPlan = tier.plan === currentPlan;
              return (
                <div key={tier.id} className="relative">
                  {isCurrentPlan && (
                    <div className="absolute -top-3 right-4 z-10">
                      <Badge className="rounded-full bg-blue-600 px-3 py-0.5 text-xs font-medium text-white hover:bg-blue-600">
                        <FormattedMessage
                          id="billing.plan.current"
                          defaultMessage="Current"
                        />
                      </Badge>
                    </div>
                  )}
                  <PricingCard
                    tier={tier}
                    onSelect={handleSelectPlan}
                    isPending={isChangingPlan}
                    isCurrentPlan={isCurrentPlan}
                    isDowngrade={
                      !isCurrentPlan &&
                      currentPlanIndex >= 0 &&
                      PLAN_ORDER.indexOf(
                        tier.plan as PreviewPlanChangeBodyOrganizationPlan
                      ) < currentPlanIndex
                    }
                    isUpgrade={
                      !isCurrentPlan &&
                      currentPlanIndex >= 0 &&
                      PLAN_ORDER.indexOf(
                        tier.plan as PreviewPlanChangeBodyOrganizationPlan
                      ) > currentPlanIndex
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between rounded-lg border p-4">
            <div>
              <h4 className="text-sm font-medium">
                <FormattedMessage
                  id="billing.portal.title"
                  defaultMessage="Billing Portal"
                />
              </h4>
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="billing.portal.description"
                  defaultMessage="View invoices, update payment method, or cancel your subscription."
                />
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleOpenPortal}
              disabled={isPortalLoading}
            >
              {isPortalLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ExternalLink className="h-4 w-4 mr-2" />
              )}
              <FormattedMessage
                id="billing.portal.open"
                defaultMessage="Open Billing Portal"
              />
            </Button>
          </div>
        </div>
      </DisplayFor>

      <PlanChangeModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        preview={preview}
        isLoadingPreview={isLoadingPreview}
        isChangingPlan={isChangingPlan}
        currentPlan={currentPlan}
        targetPlan={targetPlan}
        onConfirm={handleConfirmChange}
      />
    </div>
  );
};

const PLAN_ORDER: PreviewPlanChangeBodyOrganizationPlan[] = [
  "FLEX",
  "PRO",
  "PREMIUM",
];

function detectCurrentPlan(
  organizationInfo: Record<string, unknown> | undefined | null
): string | null {
  if (!organizationInfo) return null;
  const plan = (organizationInfo as Record<string, unknown>).organizationPlan;
  if (typeof plan !== "string") return null;
  if (plan === "LEGACY") return "FLEX";
  return plan;
}
